import { useEffect, useRef, useState } from "react";
import { ColorAddedPlayer, SortableKeys } from "../../types/IPlayerComparison";
import LabelText from "../../ui/LabelText";
import SecondaryButton from "../../ui/SecondaryButton";
import PlayerLandingCard from "../../ui/CardComponent";
import Tick from "../../assets/icons/Tick.png";
import VisibleOFF from "../../assets/icons/VisibleOFF.png";
import SortArrowUp from "../../assets/icons/sort_arrow_asc.png";
import SortArrowDown from "../../assets/icons/sort_arrow_desc.png";
import SortArrowLeft from "../../assets/icons/sort-arrow-left.png";
import SortArrowRigt from "../../assets/icons/sort-arrow-right.png";
import { IPlayerCardDetail } from "../../types/IPlayerCardDetail";
import Plus from "../../assets/icons/Plus.png";

export type IPossibleFilter =
  | "age"
  | "attackContribution"
  | "heightInCM"
  | "marketValue"
  | "starts"
  | "wages"
  | "transferValue"
  | "trueValue"
  | "finishingShots"
  | "finishingAerial"
  | "attackPositioning"
  | "creativity"
  | "passingAccuracy"
  | "dribbling"
  | "crossing"
  | "tackling"
  | "defensiveHeaders"
  | "aggression"
  | null;

export interface IPlotFilter {
  up: IPossibleFilter;
  across: IPossibleFilter;
  dotSize: IPossibleFilter;
}

export type FilterOption = keyof IPlotFilter;

const PlotFilterOptions = [
  { label: "Age", value: "age" },
  { label: "Attack contribution", value: "attackContribution" },
  { label: "Height", value: "heightInCM" },
  { label: "Total mins played", value: "minutesPlayed" },
  { label: "Market Value", value: "marketValue" },
  // { label: "Starts", value: "starts" },
  { label: "Wages", value: "wages" },
  { label: "Transfer value", value: "transferValue" },
  { label: "True value", value: "trueValue" },
  { label: "Finishing - Shots", value: "finishingShots" },
  { label: "Finishing - Aerial", value: "finishingAerial" },
  { label: "Attack positioning", value: "attackPositioning" },
  { label: "Creativity", value: "creativity" },
  { label: "Passing accuracy", value: "passingAccuracy" },
  { label: "Dribbling", value: "dribbling" },
  { label: "Crossing", value: "crossing" },
  { label: "Tackling", value: "tackling" },
  { label: "Defensive headers", value: "defensiveHeaders" },
  { label: "Ball Recovery", value: "aggression" },
];

interface PlotFilterProps {
  plotFilters: IPlotFilter;
  setSelectedPlotFilterOption: React.Dispatch<React.SetStateAction<"up" | "across" | "dotSize" | undefined>>;
  selectedPlotFilterOption: "up" | "across" | "dotSize" | undefined;
  setPlotFilters: React.Dispatch<React.SetStateAction<IPlotFilter>>;
  acrossSortDirection: "inc" | "dec";
  setAcrossSortDirection: React.Dispatch<React.SetStateAction<"inc" | "dec">>;
  upSortDirection: "desc" | "asc";
  setUpSortDirection: React.Dispatch<React.SetStateAction<"desc" | "asc">>;
}

export const PlotFilters: React.FC<PlotFilterProps> = ({
  plotFilters,
  setSelectedPlotFilterOption,
  selectedPlotFilterOption,
  setPlotFilters,
  acrossSortDirection,
  setAcrossSortDirection,
  upSortDirection,
  setUpSortDirection,
}) => {
  const handleFilterOptionChange = (option: { label: string; value: string }) => {
    plotFilters[selectedPlotFilterOption!] === option?.value
      ? setPlotFilters((prev) => ({ ...prev, [selectedPlotFilterOption!]: null }))
      : setPlotFilters((prev) => ({ ...prev, [selectedPlotFilterOption!]: option?.value }));
    const { up, across, dotSize } = plotFilters;
    const isDuplicate = up === option.value || across === option.value || dotSize === option.value;
    if (isDuplicate) {
      setPlotFilters((prev) => ({
        ...prev,
        [up === option.value ? "up" : across === option.value ? "across" : "dotSize"]: null,
      }));
    }
    setSelectedPlotFilterOption(undefined);
  };

  const handleUpSortClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    event.stopPropagation();
    if (upSortDirection === "asc") {
      setUpSortDirection("desc");
    } else {
      setUpSortDirection("asc");
    }
  };

  const handleAcrossSortClick = (event: React.MouseEvent<HTMLImageElement, MouseEvent>) => {
    event.stopPropagation();
    if (acrossSortDirection === "inc") {
      setAcrossSortDirection("dec");
    } else {
      setAcrossSortDirection("inc");
    }
  };

  return (
    <div className="comparison-tool-btns-view">
      {Object.keys(plotFilters).map((option) => (
        <SecondaryButton
          key={option}
          label={
            <>
              <span className="sort-label">{separateStringByCapitalLetters(option)}: </span>
              {plotFilters[option as FilterOption] ? (
                <LabelText className="clear-labeltext">
                  {PlotFilterOptions.find((opt) => opt.value === plotFilters[option as FilterOption])?.label || "--"}
                </LabelText>
              ) : (
                "--"
              )}
              {option === "up" && plotFilters[option as FilterOption] && (
                <img
                  className="plot-filter-icons"
                  onClick={(event) => handleUpSortClick(event)}
                  src={upSortDirection === "asc" ? SortArrowUp : SortArrowDown}
                  alt=""
                />
              )}
              {option === "across" && plotFilters[option as FilterOption] && (
                <img
                  onClick={(event) => handleAcrossSortClick(event)}
                  className="plot-filter-icons across-sort-icon"
                  src={acrossSortDirection === "inc" ? SortArrowRigt : SortArrowLeft}
                  alt=""
                />
              )}
            </>
          }
          className={plotFilters[option as FilterOption] ? "" : "plot-filter-inactive"}
          onClick={() =>
            selectedPlotFilterOption === option
              ? setSelectedPlotFilterOption(undefined)
              : setSelectedPlotFilterOption(option as FilterOption)
          }
        />
      ))}
      {selectedPlotFilterOption && (
        <div className="plot-filter-items">
          <div>{selectedPlotFilterOption?.toUpperCase()}</div>
          {PlotFilterOptions?.map((option) => (
            <div
              className="plot-filter-option"
              style={{ backgroundColor: plotFilters[selectedPlotFilterOption!] === option?.value ? "#F2F5EE" : "" }}
              onClick={() => handleFilterOptionChange(option)}
            >
              {" "}
              {option?.label}
              {plotFilters[selectedPlotFilterOption!] === option?.value && (
                <>
                  {selectedPlotFilterOption === "up" && (
                    <img
                      onClick={(event) => handleUpSortClick(event)}
                      src={upSortDirection === "asc" ? SortArrowUp : SortArrowDown}
                      alt=""
                    />
                  )}
                  {selectedPlotFilterOption === "across" && (
                    <img
                      onClick={(event) => handleAcrossSortClick(event)}
                      src={acrossSortDirection === "inc" ? SortArrowRigt : SortArrowLeft}
                      alt=""
                      className="plot-filter-option-icon"
                    />
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const separateStringByCapitalLetters = (str: string) => {
  const words = str?.split(/(?=[A-Z])/);
  const capitalizedWords = words?.map((word) => {
    return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
  });
  const result = capitalizedWords?.join(" ");
  return result;
};

export const makeStringsCapital = (str: string) => {
  const words = str?.split(/(?=[A-Z])/);
  const capitalizedWords = words?.map((word) => {
    return word.toUpperCase();
  });
  const result = capitalizedWords?.join(" ");
  return result;
};

export const generateRandomUniqueColors = (count: number) => {
  const colors: string[] = [];
  const hexChars = "0123456789ABCDEF";
  while (colors.length < count) {
    let color = "#";
    for (let i = 0; i < 6; i++) {
      color += hexChars[Math.floor(Math.random() * 16)];
    }
    if (!colors.includes(color) && color !== "#4AF075") {
      colors.push(color);
    }
  }
  return colors;
};

interface PlotPlayerSiderbarListProps {
  playerComparisonTableData: ColorAddedPlayer[];
  hoveredPlayer: number;
  setHoveredPlayer: React.Dispatch<React.SetStateAction<number>>;
  setSelectedPlayers: React.Dispatch<React.SetStateAction<number[]>>;
  selectedPlayers: number[];
  selectedSimilarPlayer: ColorAddedPlayer | null;
  hiddenPlayers: number[];
  setHiddenPlayers: React.Dispatch<React.SetStateAction<number[]>>;
  isFormSortList: boolean;
  handleRemoveSinglePlayer: (playersToRemove: number) => void;
  isChunkActive: boolean;
  isNickName: boolean;
  playerCardID: number;
  setPlayerCardID: React.Dispatch<React.SetStateAction<number>>;
  playerCardDetails: IPlayerCardDetail | undefined;
  setPlayerCardDeatils: React.Dispatch<React.SetStateAction<IPlayerCardDetail | undefined>>;
  trackerSelectedPlayer?: number[];
  setTrackerSelectedPlayer?: React.Dispatch<React.SetStateAction<number[]>>;
  setDisplayNotification?: (value: boolean) => void;
}

interface IPlayerTab {
  playerID: number;
  playerImageURL: string;
  playerLabel: string;
  colorCode: string;
  selectedPlayers: number[];
  hiddenPlayers: number[];
  hoveredPlayer: number;
  handleSelectedPlayer: (event: React.MouseEvent<HTMLDivElement, MouseEvent>, playerId: number) => void;
  isBenchmarkPlayer: boolean;
  isInjured: boolean;
  isNickName: boolean;
  isShowColorCode: boolean;
}

export const formatPlayerCode = (playerLabel: string): string => {
  if (!playerLabel) return "";
  const nameParts = playerLabel.split(" ");
  if (nameParts.length === 1) {
    return nameParts[0].slice(0, 4).toUpperCase();
  }
  const lastName = nameParts[nameParts.length - 1];
  const firstInitial = nameParts[0][0];
  const lastNamePart = lastName.slice(0, 3);
  return (firstInitial + lastNamePart).toUpperCase();
};

const PlayerTab: React.FC<IPlayerTab> = ({
  playerID,
  playerImageURL,
  playerLabel,
  colorCode,
  selectedPlayers,
  hiddenPlayers,
  hoveredPlayer,
  handleSelectedPlayer,
  isBenchmarkPlayer,
  isInjured,
  isNickName = false,
  isShowColorCode = true,
}) => {
  const [isTooltip, setIsTooltip] = useState<boolean>(false);
  const tooltipTimeout = useRef<NodeJS.Timeout | null>(null);
  const handleMouseOver = () => {
    tooltipTimeout.current = setTimeout(() => setIsTooltip(true), 500);
  };
  const handleMouseOut = () => {
    clearTimeout(tooltipTimeout.current!);
    tooltipTimeout.current = null;
    setIsTooltip(false);
  };
  return (
    <>
      <div>
        <div className="player-tab-img-container">
          {!selectedPlayers?.includes(playerID) && !hiddenPlayers?.includes(playerID) && (
            <div className="plot-player-image" onClick={(event) => handleSelectedPlayer(event, playerID)}>
              <img src={playerImageURL} alt="player-image" />
            </div>
          )}
          {hiddenPlayers?.includes(playerID) && (
            <div className="plot-player-image-hidden" onClick={(event) => handleSelectedPlayer(event, playerID)}>
              <img src={VisibleOFF} alt="hidden-player" />
            </div>
          )}
          {selectedPlayers?.includes(playerID) && !hiddenPlayers?.includes(playerID) && (
            <div className="plot-player-image-selected" onClick={(event) => handleSelectedPlayer(event, playerID)}>
              <img src={Tick} className="plot-tick-mark" alt="selected-mark" />
            </div>
          )}
          {isInjured && (
            <div className="player-tab-injury-container" onMouseOver={handleMouseOver} onMouseOut={handleMouseOut}>
              INJ
            </div>
          )}
          {isInjured && isTooltip && <div className="injured-tooltip">Currently injured</div>}
        </div>
        <div className="player-tab-name">
          <p className={hiddenPlayers?.includes(playerID) ? "hidden-player" : ""}>{playerLabel}</p>
          {isNickName && <span>{formatPlayerCode(playerLabel)}</span>}
        </div>
      </div>
    </>
  );
};

export const PlotPlayerSiderbarList: React.FC<PlotPlayerSiderbarListProps> = ({
  playerComparisonTableData,
  hoveredPlayer,
  setHoveredPlayer,
  setSelectedPlayers,
  selectedPlayers,
  selectedSimilarPlayer,
  hiddenPlayers,
  setHiddenPlayers,
  isFormSortList,
  handleRemoveSinglePlayer,
  isChunkActive,
  isNickName = false,
  playerCardID,
  setPlayerCardID,
  playerCardDetails,
  setPlayerCardDeatils,
  trackerSelectedPlayer,
  setTrackerSelectedPlayer,
  setDisplayNotification,
}) => {
  const handleSelectedPlayer = (event: React.MouseEvent<HTMLDivElement, MouseEvent>, playerId: number) => {
    event.stopPropagation();
    if (selectedPlayers?.includes(playerId)) {
      const newSelectedPlayers = selectedPlayers?.filter((playerID) => playerID !== playerId);
      setSelectedPlayers(newSelectedPlayers);
    } else if (hiddenPlayers?.includes(playerId)) {
      const newHiddenPlayers = hiddenPlayers?.filter((playerID) => playerID !== playerId);
      setHiddenPlayers(newHiddenPlayers);
    } else {
      setSelectedPlayers((prev) => [...prev, playerId]);
    }
  };
  const playerRef = useRef<HTMLDivElement>(null);
  const playerDetailsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (hoveredPlayer && playerRef.current) {
      playerRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [hoveredPlayer]);
  useEffect(() => {
    if (playerCardID && playerDetailsRef.current) {
      playerDetailsRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    }
  }, [playerCardID]);
  const [shouldShowPlusIcon, setShouldShowPlusIcon] = useState<number>(0);
  const handleAddingPlayerToTracker = (event: React.MouseEvent<HTMLDivElement | HTMLSpanElement, MouseEvent>, playerId: number) => {
    event.stopPropagation();
    if (trackerSelectedPlayer?.length === 3) {
      if (trackerSelectedPlayer.includes(playerId)) {
        setDisplayNotification!(false);
        setTrackerSelectedPlayer!(trackerSelectedPlayer.filter((id) => id !== playerId));
      } else {
        setDisplayNotification!(true);
      }
    } else if (trackerSelectedPlayer && setTrackerSelectedPlayer) {
      if (trackerSelectedPlayer.includes(playerId)) {
        setTrackerSelectedPlayer(trackerSelectedPlayer.filter((id) => id !== playerId));
      } else {
        setTrackerSelectedPlayer([...trackerSelectedPlayer, playerId]);
      }
    }
  };
  return (
    <div className={`${selectedSimilarPlayer ? "plot-player-list-benchmarked" : ""} plot-player-list`}>
      {playerComparisonTableData
        ?.filter((player) => player?.playerId !== selectedSimilarPlayer?.playerId)
        ?.map((player, i) => (
          <>
            {isChunkActive && hiddenPlayers.includes(player?.playerId) ? null : (
              <>
                {playerCardID !== player?.playerId && (
                  <div
                    key={player?.playerId}
                    ref={hoveredPlayer === player?.playerId ? playerRef : null}
                    className={`plot-player-tab ${
                      hoveredPlayer === player?.playerId || playerCardID === player?.playerId ? "plot-player-tab-hovered" : ""
                    }`}
                    onMouseOver={
                      hiddenPlayers?.includes(player?.playerId)
                        ? () => {}
                        : () => {
                            setHoveredPlayer(player?.playerId);
                            if (trackerSelectedPlayer && setTrackerSelectedPlayer) {
                              if (!trackerSelectedPlayer?.includes(player?.playerId)) {
                                setShouldShowPlusIcon(player?.playerId);
                              }
                            }
                          }
                    }
                    onMouseOut={
                      hiddenPlayers?.includes(player?.playerId)
                        ? () => {}
                        : () => {
                            setHoveredPlayer(0);
                            setShouldShowPlusIcon(0);
                          }
                    }
                    onClick={() => {
                      playerCardID === player?.playerId ? setPlayerCardID(0) : setPlayerCardID(player?.playerId);
                    }}
                  >
                    <PlayerTab
                      playerID={player?.playerId}
                      playerImageURL={player?.playerImageURL}
                      playerLabel={player?.playerLabel}
                      colorCode={player?.colorCode}
                      selectedPlayers={selectedPlayers}
                      hiddenPlayers={hiddenPlayers}
                      hoveredPlayer={hoveredPlayer}
                      handleSelectedPlayer={handleSelectedPlayer}
                      isBenchmarkPlayer={false}
                      isInjured={player?.isInjured}
                      isNickName={isNickName}
                      isShowColorCode={trackerSelectedPlayer ? (trackerSelectedPlayer?.includes(player?.playerId) ? true : false) : true}
                    />
                    {trackerSelectedPlayer &&
                      !trackerSelectedPlayer?.includes(player?.playerId) &&
                      shouldShowPlusIcon === player?.playerId && (
                        <div className="tracker-plus-container" onClick={(event) => handleAddingPlayerToTracker(event, player?.playerId)}>
                          <img src={Plus} alt="plus-icon" />
                        </div>
                      )}
                    {(!trackerSelectedPlayer || trackerSelectedPlayer?.includes(player?.playerId)) && (
                      <span
                        onClick={(event) => handleAddingPlayerToTracker(event, player?.playerId)}
                        style={{
                          backgroundColor: selectedPlayers?.includes(player?.playerId) ? "#4AF075" : player?.colorCode,
                          width: hoveredPlayer > 0 ? (hoveredPlayer === player?.playerId ? "16px" : "8px") : "16px",
                          height: hoveredPlayer > 0 ? (hoveredPlayer === player?.playerId ? "16px" : "8px") : "16px",
                          border: false ? (selectedPlayers?.includes(player?.playerId) ? "" : "2px solid #00D1A8") : "",
                          filter: false ? "drop-shadow(2px 2px 8px rgba(0, 0, 0, 0.40))" : "",
                        }}
                      ></span>
                    )}
                  </div>
                )}
                {playerCardID === player?.playerId && (
                  <div ref={playerDetailsRef}>
                    <PlayerLandingCard
                      key={playerCardDetails?.playerId!}
                      playerId={playerCardDetails?.playerId!}
                      givenName={playerCardDetails?.givenName!}
                      familyname={playerCardDetails?.familyname!}
                      playerImageURL={playerCardDetails?.playerImageURL!}
                      clubImageURL={playerCardDetails?.clubImageURL!}
                      isInjured={playerCardDetails?.isInjured!}
                      onLoan={playerCardDetails?.onLoan!}
                      primaryPlayingPosition={playerCardDetails?.primaryPlayingPosition!}
                      age={playerCardDetails?.age!}
                      heightInCM={playerCardDetails?.heightInCM!}
                      playerAttributesByPosition={playerCardDetails?.playerAttributesByPosition!}
                      contributionValue={playerCardDetails?.contributionValue!}
                      performanceValue={playerCardDetails?.performanceValue!}
                      setPlayerCardDeatils={setPlayerCardDeatils}
                      setPlayerCardID={setPlayerCardID}
                      isFormSortList={isFormSortList}
                      handleRemoveSinglePlayer={handleRemoveSinglePlayer}
                      isSelectorRequired={!selectedPlayers?.includes(player?.playerId) && !hiddenPlayers?.includes(player?.playerId)}
                      isPlayerSelected={selectedPlayers?.includes(player?.playerId) && !hiddenPlayers?.includes(player?.playerId)}
                      isPlayerHidden={hiddenPlayers?.includes(player?.playerId)}
                      handleSelectedPlayer={handleSelectedPlayer}
                      isPlayerInShortlist={playerCardDetails?.isPlayerInShortlist!}
                      isEasternPlayer={playerCardDetails?.isEasternPlayer}
                      playerLabel={playerCardDetails?.playerLabel!}
                      archetypeName={playerCardDetails?.archetypeName}
                      archetypeId={playerCardDetails?.archetypeId}
                    />
                  </div>
                )}
              </>
            )}
          </>
        ))}
      {selectedSimilarPlayer && (
        <div className="benchmark-container">
          <p>BENCHMARK</p>
          <div
            className={`plot-player-tab ${
              hoveredPlayer === selectedSimilarPlayer?.playerId || playerCardID === selectedSimilarPlayer?.playerId
                ? "plot-player-tab-hovered"
                : ""
            }`}
            onMouseOver={() => setHoveredPlayer(selectedSimilarPlayer?.playerId)}
            onMouseOut={() => setHoveredPlayer(0)}
            // onClick={() => playerCardID === selectedSimilarPlayer[0]?.playerId ? setPlayerCardID(0) : setPlayerCardID(selectedSimilarPlayer[0]?.playerId)}
          >
            <PlayerTab
              playerID={selectedSimilarPlayer?.playerId}
              playerImageURL={selectedSimilarPlayer?.playerImageURL}
              playerLabel={selectedSimilarPlayer?.playerLabel}
              colorCode={"white"}
              selectedPlayers={selectedPlayers}
              hiddenPlayers={hiddenPlayers}
              hoveredPlayer={hoveredPlayer}
              handleSelectedPlayer={handleSelectedPlayer}
              isBenchmarkPlayer={true}
              isInjured={selectedSimilarPlayer?.isInjured}
              isNickName={isNickName}
              isShowColorCode={true}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export const getPlotFilterDefaultKey = (sortKey: SortableKeys) => {
  if (sortKey.includes("FinalThird") && sortKey?.includes("finalScore")) return "finishingShots";
  if (sortKey?.includes("BuildUpPlay") && sortKey?.includes("finalScore")) return "creativity";
  if (sortKey?.includes("Defence") && sortKey?.includes("finalScore")) return "tackling";
  if (sortKey?.includes("attackPositioning")) return "attackPositioning";
  if (sortKey?.includes("finishingAerial")) return "finishingAerial";
  if (sortKey?.includes("finishingShots")) return "finishingShots";
  if (sortKey?.includes("passingAccuracy")) return "passingAccuracy";
  if (sortKey?.includes("creativity")) return "creativity";
  if (sortKey?.includes("dribbling")) return "dribbling";
  if (sortKey?.includes("crossing")) return "crossing";
  if (sortKey?.includes("tackling")) return "tackling";
  if (sortKey?.includes("defensiveHeaders")) return "defensiveHeaders";
  if (sortKey?.includes("attackContribution")) return "attackContribution";
  if (sortKey?.includes("aggression")) return "aggression";
  if (sortKey?.includes("heightInCM")) return "heightInCM";
  if (sortKey?.includes("transferValue")) return "transferValue";
  if (sortKey?.includes("trueValue")) return "trueValue";
  if (sortKey?.includes("marketValue")) return "marketValue";
  if (sortKey?.includes("contract")) return "attackContribution";
  if (sortKey?.includes("clubName")) return "attackContribution";
  if (sortKey?.includes("nationality[0].name")) return "attackContribution";
  if (sortKey?.includes("contract.months")) return "attackContribution";
  if (sortKey?.includes("starts")) return "starts";
  if (sortKey?.includes("playerLabel")) return "attackContribution";
  if (sortKey === "wages") return "wages";
  if (sortKey === "age") return "age";
  return "attackContribution";
};
